// The final-score reveal, as a pure plan.
//
// Bug report (Matt, 2026-08-28): "There should be more drama for revealing
// the final scores... We should make it more fun for the scores to show up."
// So the scoreboard turns over one row at a time, LAST place first, with a
// drumroll beat before the winner - the way a real award count-up works.
//
// This module is deliberately framework-free so the sequencing is testable
// the way the engine is: buildRevealPlan(rows) -> ordered steps, and the
// component just walks them with setTimeout. Total run for a 4-player game
// is about five seconds, and a tap skips straight to the end - drama must
// never become a toll on the fiftieth play.

export const REVEAL_TIMING = {
  first: 500, // beat before the first (last-place) row turns over
  step: 750, // each further row after that
  drumroll: 1000, // the "and the richest is…" beat
  winners: 1200, // the winner row(s) themselves
  done: 700, // beat before the Again/Done buttons return
};

/**
 * `rows` is scoreboard() output, best-first. Returns the steps of the reveal
 * in play order. Each step carries:
 *   kind   - 'row' | 'drumroll' | 'winners' | 'done'
 *   after  - ms to wait after the previous step before applying this one
 *   reveal - indexes (into `rows`) that turn face-up at this step
 *
 * Worst place turns over first. Every winner - rank 1 can be a tie - turns
 * over together at the end, so a tie is revealed as a tie instead of one
 * winner scooping the moment.
 */
export function buildRevealPlan(rows, timing = REVEAL_TIMING) {
  const winnersCount = rows.filter((row) => row.isWinner).length;
  const steps = [];

  for (let i = rows.length - 1; i >= winnersCount; i -= 1) {
    steps.push({
      kind: 'row',
      after: steps.length ? timing.step : timing.first,
      reveal: [i],
    });
  }

  steps.push({ kind: 'drumroll', after: timing.drumroll, reveal: [] });
  steps.push({
    kind: 'winners',
    after: timing.winners,
    reveal: Array.from({ length: winnersCount }, (_, i) => i),
  });
  steps.push({ kind: 'done', after: timing.done, reveal: [] });

  return steps;
}
